import { useEffect, useState } from 'react'
import { Icon } from '@/components/ui/Icon'

const PREFERENCE_SUGGESTIONS = [
  'No horror or PvP',
  'Max 90-min sessions',
  'Story-light, low grind',
  'Crossplay required',
]

export interface RecTuningProps {
  groupName: string
  groupGuidance: string
  isSavingGuidance: boolean
  onSaveGuidance: (text: string) => void
  canEdit?: boolean
}

export function RecTuning({
  groupName,
  groupGuidance,
  isSavingGuidance,
  onSaveGuidance,
  canEdit = true,
}: RecTuningProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(groupGuidance)
  const [collapsed, setCollapsed] = useState(true)

  useEffect(() => {
    if (editing) setCollapsed(false)
  }, [editing])

  useEffect(() => {
    if (editing) setDraft(groupGuidance)
  }, [editing, groupGuidance])

  const save = () => {
    onSaveGuidance(draft.trim())
    setEditing(false)
  }
  const cancel = () => {
    setDraft(groupGuidance)
    setEditing(false)
  }
  const startEdit = () => {
    setDraft(groupGuidance)
    setEditing(true)
  }
  const appendSuggestion = (s: string) => {
    setDraft((d) => {
      if (!d) return s + '.'
      const trimmed = d.trimEnd()
      const sep = /[.!?]$/.test(trimmed) ? ' ' : '. '
      return trimmed + sep + s + '.'
    })
  }

  return (
    <section
      className={`cg-tuning${collapsed ? ' collapsed' : ''}`}
      aria-label="LLM tuning"
    >
      <button
        type="button"
        className="cg-tuning-head"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <div className="cg-tuning-head-left">
          <span className="cg-tuning-title">
            <Icon name="spark" size={12} />
            <span>Tune recommendations</span>
          </span>
          {collapsed ? (
            <span className="cg-tuning-summary">
              {groupGuidance ? (
                <span
                  className="cg-tuning-summary-chip persistent"
                  title={groupGuidance}
                >
                  <span className="cg-tuning-summary-dot" />
                  <span className="cg-tuning-summary-text">{groupGuidance}</span>
                </span>
              ) : (
                <span className="cg-tuning-summary-chip cta">
                  <Icon name="plus" size={10} />
                  <span>
                    Add group preferences to tune picks for <strong>{groupName}</strong>
                  </span>
                </span>
              )}
            </span>
          ) : (
            <span className="cg-tuning-sub">
              Guide how <strong>{groupName}</strong>'s suggestions are picked.
            </span>
          )}
        </div>
        <span className="cg-tuning-toggle" aria-hidden="true">
          <span>{collapsed ? 'Expand' : 'Collapse'}</span>
          <Icon name={collapsed ? 'expand' : 'collapse'} size={11} />
        </span>
      </button>

      {!collapsed && (
        <div className="cg-tuning-body">
          <div className="cg-tuning-row">
            <div className="cg-tuning-row-head">
              <div className="cg-tuning-row-meta">
                <div className="cg-tuning-row-label">Group preferences</div>
                <div className="cg-tuning-row-hint">
                  Applied to every recommendation refresh for this group.
                </div>
              </div>
              {!editing && canEdit && (
                <button
                  type="button"
                  className="cg-btn ghost tiny"
                  onClick={startEdit}
                >
                  {groupGuidance ? 'Edit' : '+ Add'}
                </button>
              )}
            </div>

            {editing ? (
              <div className="cg-tuning-edit">
                <textarea
                  className="cg-tuning-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={`What should we know about ${groupName}? e.g. "Prefer co-op with progression, no horror, max 90 min sessions."`}
                  rows={4}
                  maxLength={500}
                  autoFocus
                />
                <div className="cg-tuning-suggest">
                  {PREFERENCE_SUGGESTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      className="cg-tuning-chip"
                      onClick={() => appendSuggestion(s)}
                    >
                      + {s}
                    </button>
                  ))}
                </div>
                <div className="cg-tuning-actions">
                  <button
                    type="button"
                    className="cg-btn ghost tiny"
                    onClick={cancel}
                    disabled={isSavingGuidance}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cg-btn primary tiny"
                    onClick={save}
                    disabled={isSavingGuidance}
                  >
                    <Icon name="check" size={11} /> Save preferences
                  </button>
                </div>
              </div>
            ) : groupGuidance ? (
              <div className="cg-tuning-read">
                <span className="cg-tuning-quote" aria-hidden="true">
                  “
                </span>
                {groupGuidance}
                <span className="cg-tuning-quote close" aria-hidden="true">
                  ”
                </span>
              </div>
            ) : (
              <button type="button" className="cg-tuning-empty" onClick={startEdit}>
                <Icon name="plus" size={11} />
                <span>
                  Add preferences so picks feel tuned to <strong>{groupName}</strong>
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
